/**
 * User Menu Component
 *
 * Displays user profile dropdown with settings, preferences, and logout options.
 * Also shows organization switcher for multi-org users.
 */

import { LitElement, html, css } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import type { User, Organization, CustomMenuItem, UserStatus, QuickSettings, MenuItemSnapshot } from '../types';
import { authAPI, portalAPI, presenceAPI, settingsAPI, getToken } from '../services/api';
import { eventBus, Events } from '../services/events';

@customElement('moya-user-menu')
export class MoyaUserMenu extends LitElement {
  static styles = css`
    :host {
      display: inline-block;
      position: relative;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }

    .trigger {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 4px 8px;
      border: none;
      background: transparent;
      border-radius: 8px;
      cursor: pointer;
      color: currentColor;
      transition: background-color 0.15s ease;
    }

    .trigger:hover {
      background: rgba(255, 255, 255, 0.1);
    }

    /* Visible keyboard focus indicator (WCAG 2.4.7) */
    .trigger:focus-visible {
      outline: 2px solid #60a5fa;
      outline-offset: 2px;
      box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.5);
    }

    .trigger.open {
      background: rgba(255, 255, 255, 0.15);
    }

    .avatar {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 13px;
      font-weight: 600;
      color: white;
      flex-shrink: 0;
    }

    .avatar.gradient {
      background: linear-gradient(135deg, #8b5cf6, #ec4899);
    }

    .avatar img {
      width: 100%;
      height: 100%;
      border-radius: 50%;
      object-fit: cover;
    }

    /* Status indicator on avatar */
    .avatar-wrapper {
      position: relative;
      display: inline-block;
    }

    .status-dot {
      position: absolute;
      bottom: 0;
      right: 0;
      width: 10px;
      height: 10px;
      border-radius: 50%;
      border: 2px solid #1e293b;
      box-sizing: border-box;
    }

    .status-dot.online { background-color: #22c55e; }
    .status-dot.away { background-color: #f59e0b; }
    .status-dot.busy { background-color: #ef4444; }
    .status-dot.dnd { background-color: #ef4444; }
    .status-dot.offline { background-color: #6b7280; }

    .status-dot.size-sm {
      width: 8px;
      height: 8px;
      border-width: 1.5px;
    }

    .status-dot.size-lg {
      width: 12px;
      height: 12px;
      border-width: 2px;
    }

    /* Profile status indicator (larger) */
    .profile-avatar-wrapper {
      position: relative;
      display: inline-block;
    }

    .profile-status-dot {
      position: absolute;
      bottom: 2px;
      right: 2px;
      width: 14px;
      height: 14px;
      border-radius: 50%;
      border: 2px solid #1e293b;
      box-sizing: border-box;
    }

    .profile-status-dot.online { background-color: #22c55e; }
    .profile-status-dot.away { background-color: #f59e0b; }
    .profile-status-dot.busy { background-color: #ef4444; }
    .profile-status-dot.dnd { background-color: #ef4444; }
    .profile-status-dot.offline { background-color: #6b7280; }

    .user-info {
      display: none;
      flex-direction: column;
      align-items: flex-start;
      min-width: 0;
    }

    @media (min-width: 640px) {
      .user-info {
        display: flex;
      }
    }

    .user-name {
      font-size: 13px;
      font-weight: 500;
      color: white;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 120px;
    }

    .user-email {
      font-size: 11px;
      color: rgba(255, 255, 255, 0.5);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 120px;
    }

    .chevron {
      width: 16px;
      height: 16px;
      color: rgba(255, 255, 255, 0.5);
      transition: transform 0.15s ease;
    }

    .trigger.open .chevron {
      transform: rotate(180deg);
    }

    .dropdown {
      position: absolute;
      top: calc(100% + 8px);
      right: 0;
      width: 280px;
      background: #1e293b;
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 12px;
      box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
      overflow: hidden;
      z-index: 1000;
      display: none;
    }

    .dropdown.open {
      display: block;
    }

    .profile-section {
      padding: 16px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      text-align: center;
    }

    .profile-avatar {
      width: 56px;
      height: 56px;
      border-radius: 50%;
      margin: 0 auto 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 20px;
      font-weight: 600;
      color: white;
    }

    .profile-name {
      font-size: 15px;
      font-weight: 600;
      color: white;
      margin-bottom: 2px;
    }

    .profile-email {
      font-size: 13px;
      color: rgba(255, 255, 255, 0.5);
    }

    .org-section {
      padding: 8px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }

    .org-label {
      padding: 4px 8px;
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: rgba(255, 255, 255, 0.4);
    }

    .org-item {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 8px;
      border-radius: 6px;
      cursor: pointer;
      transition: background-color 0.15s ease;
    }

    .org-item:hover {
      background: rgba(255, 255, 255, 0.05);
    }

    .org-item:focus-visible {
      outline: 2px solid #60a5fa;
      outline-offset: -2px;
      background: rgba(255, 255, 255, 0.08);
    }

    .org-item.active {
      background: rgba(59, 130, 246, 0.1);
    }

    .org-logo {
      width: 28px;
      height: 28px;
      border-radius: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      font-weight: 600;
      background: rgba(255, 255, 255, 0.1);
      color: white;
    }

    .org-name {
      flex: 1;
      font-size: 13px;
      color: white;
    }

    .org-check {
      color: #3b82f6;
      font-size: 14px;
    }

    .menu-section {
      padding: 8px;
    }

    .menu-item {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 10px 8px;
      border-radius: 6px;
      cursor: pointer;
      transition: background-color 0.15s ease;
      color: rgba(255, 255, 255, 0.9);
      font-size: 13px;
      text-decoration: none;
    }

    .menu-item:hover {
      background: rgba(255, 255, 255, 0.05);
    }

    .menu-item:focus-visible {
      outline: 2px solid #60a5fa;
      outline-offset: -2px;
      background: rgba(255, 255, 255, 0.08);
    }

    .menu-item.kb-active {
      background: rgba(59, 130, 246, 0.18);
      outline: 2px solid #60a5fa;
      outline-offset: -2px;
    }

    .menu-item.danger {
      color: #ef4444;
    }

    .menu-item.danger:hover {
      background: rgba(239, 68, 68, 0.1);
    }

    .menu-item.disabled {
      opacity: 0.4;
      cursor: not-allowed;
      pointer-events: none;
    }

    .menu-item-label {
      flex: 1;
      min-width: 0;
    }

    .menu-item-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 18px;
      height: 18px;
      padding: 0 5px;
      border-radius: 9px;
      background: #ef4444;
      color: white;
      font-size: 11px;
      font-weight: 700;
      line-height: 1;
    }

    .menu-icon {
      width: 18px;
      height: 18px;
      opacity: 0.7;
    }

    .divider {
      height: 1px;
      background: rgba(255, 255, 255, 0.1);
      margin: 4px 8px;
    }

    /* Avatar size variants */
    .avatar.size-sm {
      width: 24px;
      height: 24px;
      font-size: 10px;
    }

    .avatar.size-md {
      width: 32px;
      height: 32px;
      font-size: 13px;
    }

    .avatar.size-lg {
      width: 40px;
      height: 40px;
      font-size: 15px;
    }

    /* Avatar-only mode */
    .trigger.avatar-only {
      padding: 4px;
    }

    .trigger.avatar-only .chevron {
      display: none;
    }

    /* CSS Variables for theming */
    :host {
      --moya-user-menu-avatar-size: 32px;
      --moya-user-menu-dropdown-bg: #1e293b;
      --moya-user-menu-text-primary: white;
      --moya-user-menu-text-secondary: rgba(255, 255, 255, 0.5);
      --moya-user-menu-hover-bg: rgba(255, 255, 255, 0.05);
      --moya-user-menu-active-bg: rgba(59, 130, 246, 0.1);
    }

    .dropdown {
      background: var(--moya-user-menu-dropdown-bg);
    }

    .profile-name,
    .user-name,
    .org-name {
      color: var(--moya-user-menu-text-primary);
    }

    .profile-email,
    .user-email {
      color: var(--moya-user-menu-text-secondary);
    }

    /* Custom menu item icon */
    .custom-icon {
      width: 18px;
      height: 18px;
      opacity: 0.7;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .custom-icon svg {
      width: 100%;
      height: 100%;
    }

    /* Status selector section */
    .status-section {
      padding: 8px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }

    .status-current {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px;
      border-radius: 6px;
      cursor: pointer;
      transition: background-color 0.15s ease;
    }

    .status-current:hover {
      background: rgba(255, 255, 255, 0.05);
    }

    .status-current:focus-visible {
      outline: 2px solid #60a5fa;
      outline-offset: -2px;
    }

    .status-current-left {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .status-indicator {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      flex-shrink: 0;
    }

    .status-indicator.online { background-color: #22c55e; }
    .status-indicator.away { background-color: #f59e0b; }
    .status-indicator.busy { background-color: #ef4444; }
    .status-indicator.dnd { background-color: #ef4444; }
    .status-indicator.offline { background-color: #6b7280; }

    .status-text {
      font-size: 13px;
      color: rgba(255, 255, 255, 0.9);
    }

    .status-message {
      font-size: 11px;
      color: rgba(255, 255, 255, 0.5);
      margin-top: 2px;
    }

    .status-chevron {
      width: 16px;
      height: 16px;
      color: rgba(255, 255, 255, 0.5);
      transition: transform 0.15s ease;
    }

    .status-current.expanded .status-chevron {
      transform: rotate(180deg);
    }

    /* Status dropdown options */
    .status-options {
      display: none;
      padding: 4px 0;
    }

    .status-options.open {
      display: block;
    }

    .status-option {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 8px;
      border-radius: 6px;
      cursor: pointer;
      transition: background-color 0.15s ease;
      font-size: 13px;
      color: rgba(255, 255, 255, 0.9);
    }

    .status-option:hover {
      background: rgba(255, 255, 255, 0.05);
    }

    .status-option:focus-visible {
      outline: 2px solid #60a5fa;
      outline-offset: -2px;
    }

    .status-option.active {
      background: rgba(59, 130, 246, 0.1);
    }

    .status-option .status-check {
      margin-left: auto;
      color: #3b82f6;
      font-size: 14px;
    }

    .set-custom-status {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 8px;
      border-radius: 6px;
      cursor: pointer;
      transition: background-color 0.15s ease;
      font-size: 13px;
      color: rgba(255, 255, 255, 0.7);
      border-top: 1px solid rgba(255, 255, 255, 0.05);
      margin-top: 4px;
    }

    .set-custom-status:hover {
      background: rgba(255, 255, 255, 0.05);
      color: rgba(255, 255, 255, 0.9);
    }

    /* Quick settings section */
    .quick-settings-section {
      padding: 8px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    }

    .quick-settings-label {
      padding: 4px 8px;
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: rgba(255, 255, 255, 0.4);
    }

    .quick-setting-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 8px;
      border-radius: 6px;
    }

    .quick-setting-left {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .quick-setting-icon {
      width: 18px;
      height: 18px;
      opacity: 0.7;
    }

    .quick-setting-label {
      font-size: 13px;
      color: rgba(255, 255, 255, 0.9);
    }

    /* Toggle switch */
    .toggle-switch {
      position: relative;
      width: 36px;
      height: 20px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 10px;
      cursor: pointer;
      transition: background-color 0.2s ease;
    }

    .toggle-switch:focus-visible {
      outline: 2px solid #60a5fa;
      outline-offset: 2px;
    }

    .toggle-switch.on {
      background: #3b82f6;
    }

    .toggle-switch::after {
      content: '';
      position: absolute;
      top: 2px;
      left: 2px;
      width: 16px;
      height: 16px;
      background: white;
      border-radius: 50%;
      transition: transform 0.2s ease;
    }

    .toggle-switch.on::after {
      transform: translateX(16px);
    }

    /* Custom status input */
    .custom-status-input {
      display: none;
      padding: 8px;
    }

    .custom-status-input.open {
      display: block;
    }

    .custom-status-input input {
      width: 100%;
      padding: 8px 12px;
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 6px;
      color: white;
      font-size: 13px;
      outline: none;
    }

    .custom-status-input input:focus {
      border-color: #3b82f6;
    }

    .custom-status-input input::placeholder {
      color: rgba(255, 255, 255, 0.4);
    }

    .custom-status-actions {
      display: flex;
      gap: 8px;
      margin-top: 8px;
    }

    .custom-status-btn {
      flex: 1;
      padding: 6px 12px;
      border: none;
      border-radius: 4px;
      font-size: 12px;
      cursor: pointer;
      transition: background-color 0.15s ease;
    }

    .custom-status-btn.primary {
      background: #3b82f6;
      color: white;
    }

    .custom-status-btn.primary:hover {
      background: #2563eb;
    }

    .custom-status-btn.secondary {
      background: rgba(255, 255, 255, 0.1);
      color: rgba(255, 255, 255, 0.9);
    }

    .custom-status-btn.secondary:hover {
      background: rgba(255, 255, 255, 0.15);
    }

    @media (max-width: 640px) {
      .dropdown {
        width: min(280px, calc(100vw - 32px));
        right: -8px;
      }

      .menu-item {
        min-height: 44px;
      }

      .status-option,
      .org-item {
        min-height: 44px;
      }
    }
  `;

  @property({ type: String }) token = '';
  @property({ type: Object }) user: User | null = null;

  // Display options
  @property({ type: Boolean }) avatarOnly = false;
  @property({ type: String }) size: 'sm' | 'md' | 'lg' = 'md';

  // URL configuration
  @property({ type: String }) settingsUrl = 'https://login.moyasolutions.com/settings?tab=preferences';
  @property({ type: String }) profileUrl = 'https://login.moyasolutions.com/settings?tab=profile';
  @property({ type: String }) notificationPrefsUrl = 'https://login.moyasolutions.com/settings?tab=notifications';
  @property({ type: String }) helpUrl = '/help';

  // Feature toggles
  @property({ type: Boolean }) showProfile = true;
  @property({ type: Boolean }) showSettings = true;
  @property({ type: Boolean }) showNotificationPrefs = true;
  @property({ type: Boolean }) showHelp = true;
  @property({ type: Boolean }) showOrganizations = true;

  // Custom menu items
  @property({ type: Array }) customMenuItems: CustomMenuItem[] = [];

  // App-registered menu items in the 'user-menu' region, passed through from
  // <moya-platform-header>. Rendered alongside customMenuItems; a non-href item
  // dispatches 'moya-menu-select' { itemId } (bubbles + composed).
  @property({ type: Array }) registeredMenuItems: MenuItemSnapshot[] = [];

  // Status selector
  @property({ type: Boolean }) showStatusSelector = true;
  @property({ type: Array }) statusOptions: UserStatus[] = ['online', 'away', 'busy', 'dnd'];

  // Quick settings
  @property({ type: Boolean }) showQuickSettings = true;
  @property({ type: Object }) quickSettings: QuickSettings = {
    notifications: true,
    darkMode: false,
    sounds: true,
  };

  @state() private isOpen = false;
  @state() private statusDropdownOpen = false;
  @state() private customStatusInputOpen = false;
  @state() private customStatusMessage = '';
  @state() private currentStatus: UserStatus = 'online';

  @query('.trigger') private triggerEl!: HTMLButtonElement;
  @query('.dropdown') private dropdownEl!: HTMLDivElement;

  private clickOutsideHandler: ((e: MouseEvent) => void) | null = null;

  connectedCallback() {
    super.connectedCallback();
    this.clickOutsideHandler = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.clickOutsideHandler);

    // Load user if not provided - wait a tick to allow parent to configure API
    if (!this.user && this.token) {
      // Defer loading to allow parent platform-header to configure the API service first
      setTimeout(() => this.loadUser(), 50);
    }

    // Sync current status from user if available
    if (this.user?.status) {
      this.currentStatus = this.user.status;
    }
    if (this.user?.statusMessage) {
      this.customStatusMessage = this.user.statusMessage;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.clickOutsideHandler) {
      document.removeEventListener('click', this.clickOutsideHandler);
    }
  }

  // Track if we've loaded user to avoid duplicate calls
  private userLoadAttempted = false;

  updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);
    // If token was just set and we haven't loaded user yet, try to load
    if (changedProperties.has('token') && this.token && !this.user && !this.userLoadAttempted) {
      this.userLoadAttempted = true;
      // Give parent time to call setToken() with the new value
      setTimeout(() => this.loadUser(), 100);
    }

    // When opening the dropdown, focus the first menu item for keyboard users
    if (changedProperties.has('isOpen') && this.isOpen) {
      requestAnimationFrame(() => {
        const first = this.dropdownEl?.querySelector<HTMLElement>('[role="menuitem"]');
        first?.focus();
      });
    }
  }

  private async loadUser() {
    try {
      this.user = await authAPI.getCurrentUser();
      eventBus.emit(Events.USER_LOADED, this.user);
    } catch (e) {
      console.error('[UserMenu] Failed to load user from API, falling back to token:', e);
      // Fallback: decode JWT to get user info
      this.user = this.decodeUserFromToken();
      if (this.user) {
        eventBus.emit(Events.USER_LOADED, this.user);
      }
    }
  }

  private decodeUserFromToken(): User | null {
    try {
      const token = getToken();
      if (!token) return null;

      // Decode JWT payload (base64url encoded)
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      const payload = parts[1];
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
      const claims = JSON.parse(decoded);

      // Try multiple JWT claim fields for the display name
      const name = claims.name
        || claims.display_name
        || claims.preferred_username
        || claims.given_name
        || (claims.given_name && claims.family_name ? `${claims.given_name} ${claims.family_name}` : null)
        || claims.email?.split('@')[0]
        || 'User';

      return {
        id: claims.sub || '',
        email: claims.email || '',
        name,
        avatarUrl: claims.picture || claims.avatar_url || undefined,
        roles: claims.roles || claims.groups || [],
        organizations: [],
        activeOrganizationId: undefined,
      };
    } catch (e) {
      console.error('[UserMenu] Failed to decode token:', e);
      return null;
    }
  }

  private handleClickOutside(e: MouseEvent) {
    if (this.isOpen && !this.contains(e.target as Node)) {
      this.isOpen = false;
    }
  }

  private toggleDropdown(e: Event) {
    e.stopPropagation();
    this.isOpen = !this.isOpen;
  }

  /** Roving keyboard navigation across the menu. */
  private handleMenuKeydown(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      if (this.isOpen) {
        e.preventDefault();
        this.isOpen = false;
        this.statusDropdownOpen = false;
        this.customStatusInputOpen = false;
        this.triggerEl?.focus();
      }
      return;
    }

    if (!this.dropdownEl) return;
    const items = Array.from(
      this.dropdownEl.querySelectorAll<HTMLElement>('[role="menuitem"]')
    );
    if (items.length === 0) return;

    // Focus lives inside the shadow root, so document.activeElement returns the
    // host element (always -1 here). Read the shadow root's activeElement so
    // roving focus actually steps through the menu items.
    const active = this.shadowRoot?.activeElement as HTMLElement | null;
    const currentIndex = active ? items.indexOf(active) : -1;

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        const next = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
        items[next]?.focus();
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const prev = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
        items[prev]?.focus();
        break;
      }
      case 'Home':
        e.preventDefault();
        items[0]?.focus();
        break;
      case 'End':
        e.preventDefault();
        items[items.length - 1]?.focus();
        break;
      case 'Enter':
      case ' ':
        if (active && items.includes(active)) {
          e.preventDefault();
          active.click();
        }
        break;
    }
  }

  /** Handle keydown on the trigger button itself. */
  private handleTriggerKeydown(e: KeyboardEvent) {
    if ((e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') && !this.isOpen) {
      e.preventDefault();
      this.isOpen = true;
    } else if (e.key === 'Escape' && this.isOpen) {
      e.preventDefault();
      this.isOpen = false;
    }
  }

  private getInitials(name?: string, email?: string): string {
    if (name) {
      const parts = name.split(' ');
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    }
    if (email) {
      return email.substring(0, 2).toUpperCase();
    }
    return '?';
  }

  private getAvatarColor(name?: string): string {
    const colors = [
      'linear-gradient(135deg, #8b5cf6, #ec4899)',
      'linear-gradient(135deg, #3b82f6, #06b6d4)',
      'linear-gradient(135deg, #10b981, #3b82f6)',
      'linear-gradient(135deg, #f59e0b, #ef4444)',
      'linear-gradient(135deg, #ec4899, #8b5cf6)',
    ];
    if (!name) return colors[0];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  }

  private async handleOrgSwitch(org: Organization) {
    if (org.id === this.user?.activeOrganizationId) {
      return;
    }

    try {
      await portalAPI.switchOrganization(org.id);

      if (this.user) {
        this.user = { ...this.user, activeOrganizationId: org.id };
      }

      this.dispatchEvent(
        new CustomEvent('moya-organization-switch', {
          bubbles: true,
          composed: true,
          detail: { organization: org },
        })
      );

      eventBus.emit(Events.ORGANIZATION_SWITCHED, org);

      // Reload page to reflect organization change
      window.location.reload();
    } catch (e) {
      console.error('[UserMenu] Failed to switch organization:', e);
    }
  }

  private async handleLogout() {
    try {
      await authAPI.logout();
    } catch (e) {
      console.error('[UserMenu] Logout API call failed:', e);
    }

    // Clear all auth state: localStorage tokens + domain cookie
    localStorage.removeItem('token');
    localStorage.removeItem('moya_token');
    localStorage.removeItem('moya_user');
    localStorage.removeItem('user_id');
    // Clear domain-wide SSO cookie
    document.cookie = 'moya_token=; Domain=.moyasolutions.com; Path=/; Max-Age=0; Secure; SameSite=Lax';

    this.dispatchEvent(
      new CustomEvent('moya-logout', {
        bubbles: true,
        composed: true,
      })
    );

    eventBus.emit(Events.USER_LOGOUT);

    // Redirect to SSO portal login
    // Always redirect to SSO portal for logout (not identity API)
    window.location.href = 'https://login.moyasolutions.com/logout';
  }

  private handleCustomItemClick(item: CustomMenuItem) {
    if (item.action) {
      this.dispatchEvent(
        new CustomEvent(item.action, {
          bubbles: true,
          composed: true,
          detail: { item },
        })
      );
    }
    this.isOpen = false;
  }

  private getStatusLabel(status: UserStatus): string {
    const labels: Record<UserStatus, string> = {
      online: 'Online',
      away: 'Away',
      busy: 'Busy',
      dnd: 'Do Not Disturb',
      offline: 'Offline',
    };
    return labels[status] || status;
  }

  private toggleStatusDropdown(e: Event) {
    e.stopPropagation();
    this.statusDropdownOpen = !this.statusDropdownOpen;
    if (!this.statusDropdownOpen) {
      this.customStatusInputOpen = false;
    }
  }

  private async handleStatusChange(status: UserStatus) {
    const previousStatus = this.currentStatus;
    this.currentStatus = status;
    this.statusDropdownOpen = false;

    try {
      await presenceAPI.updateStatus(status, this.customStatusMessage || undefined);

      if (this.user) {
        this.user = { ...this.user, status };
      }

      this.dispatchEvent(
        new CustomEvent('moya-status-change', {
          bubbles: true,
          composed: true,
          detail: { status, statusMessage: this.customStatusMessage },
        })
      );

      eventBus.emit(Events.USER_STATUS_CHANGED, { status, statusMessage: this.customStatusMessage });
    } catch (e) {
      console.error('[UserMenu] Failed to update status:', e);
      // Revert on error
      this.currentStatus = previousStatus;
    }
  }

  private openCustomStatusInput(e: Event) {
    e.stopPropagation();
    this.customStatusInputOpen = true;
  }

  private async handleCustomStatusSubmit() {
    try {
      await presenceAPI.updateStatus(this.currentStatus, this.customStatusMessage);

      if (this.user) {
        this.user = { ...this.user, statusMessage: this.customStatusMessage };
      }

      this.dispatchEvent(
        new CustomEvent('moya-status-change', {
          bubbles: true,
          composed: true,
          detail: { status: this.currentStatus, statusMessage: this.customStatusMessage },
        })
      );

      eventBus.emit(Events.USER_STATUS_CHANGED, {
        status: this.currentStatus,
        statusMessage: this.customStatusMessage
      });
    } catch (e) {
      console.error('[UserMenu] Failed to set custom status:', e);
    }
    this.customStatusInputOpen = false;
  }

  private cancelCustomStatus() {
    this.customStatusInputOpen = false;
    this.customStatusMessage = this.user?.statusMessage || '';
  }

  private async handleQuickSettingToggle(setting: keyof QuickSettings) {
    const newValue = !this.quickSettings[setting];
    this.quickSettings = { ...this.quickSettings, [setting]: newValue };

    // Emit setting changed event
    this.dispatchEvent(
      new CustomEvent('moya-setting-changed', {
        bubbles: true,
        composed: true,
        detail: { setting, value: newValue },
      })
    );

    eventBus.emit(Events.SETTING_CHANGED, { setting, value: newValue });

    // Special handling for dark mode
    if (setting === 'darkMode') {
      this.dispatchEvent(
        new CustomEvent('moya-theme-changed', {
          bubbles: true,
          composed: true,
          detail: { theme: newValue ? 'dark' : 'light' },
        })
      );
      eventBus.emit(Events.THEME_CHANGED, { theme: newValue ? 'dark' : 'light' });
    }

    // Persist to API (fire and forget)
    try {
      await settingsAPI.updateSetting(setting, newValue);
    } catch (e) {
      console.error('[UserMenu] Failed to save setting:', e);
    }
  }

  private renderStatusSelector() {
    if (!this.showStatusSelector) return '';

    const currentStatusClasses = classMap({
      'status-current': true,
      expanded: this.statusDropdownOpen,
    });

    const optionsClasses = classMap({
      'status-options': true,
      open: this.statusDropdownOpen,
    });

    const customInputClasses = classMap({
      'custom-status-input': true,
      open: this.customStatusInputOpen,
    });

    return html`
      <div class="status-section">
        <div
          class="${currentStatusClasses}"
          @click="${this.toggleStatusDropdown}"
          role="button"
          tabindex="0"
          aria-haspopup="menu"
          aria-expanded="${this.statusDropdownOpen ? 'true' : 'false'}"
          aria-label="Change presence status, currently ${this.getStatusLabel(this.currentStatus)}"
          @keydown="${(e: KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              this.toggleStatusDropdown(e);
            }
          }}"
        >
          <div class="status-current-left">
            <span class="status-indicator ${this.currentStatus}" aria-hidden="true"></span>
            <div>
              <div class="status-text">${this.getStatusLabel(this.currentStatus)}</div>
              ${this.customStatusMessage
                ? html`<div class="status-message">${this.customStatusMessage}</div>`
                : ''}
            </div>
          </div>
          <svg class="status-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </div>

        <div class="${optionsClasses}" role="menu" aria-label="Presence statuses">
          ${this.statusOptions.map(
            (status) => html`
              <div
                class="status-option ${status === this.currentStatus ? 'active' : ''}"
                @click="${() => this.handleStatusChange(status)}"
                role="menuitemradio"
                tabindex="${this.statusDropdownOpen ? '0' : '-1'}"
                aria-checked="${status === this.currentStatus ? 'true' : 'false'}"
                aria-label="Set status to ${this.getStatusLabel(status)}"
                @keydown="${(e: KeyboardEvent) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    this.handleStatusChange(status);
                  }
                }}"
              >
                <span class="status-indicator ${status}" aria-hidden="true"></span>
                <span>${this.getStatusLabel(status)}</span>
                ${status === this.currentStatus ? html`<span class="status-check" aria-hidden="true">✓</span>` : ''}
              </div>
            `
          )}
          <div
            class="set-custom-status"
            @click="${this.openCustomStatusInput}"
            role="menuitem"
            tabindex="${this.statusDropdownOpen ? '0' : '-1'}"
            aria-label="Set custom status message"
            @keydown="${(e: KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.openCustomStatusInput(e);
              }
            }}"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <line x1="12" y1="5" x2="12" y2="19" stroke-linecap="round" />
              <line x1="5" y1="12" x2="19" y2="12" stroke-linecap="round" />
            </svg>
            Set custom status...
          </div>
        </div>

        <div class="${customInputClasses}">
          <input
            type="text"
            placeholder="What's your status?"
            aria-label="Custom status message"
            .value="${this.customStatusMessage}"
            @input="${(e: Event) => (this.customStatusMessage = (e.target as HTMLInputElement).value)}"
            @keydown="${(e: KeyboardEvent) => {
              if (e.key === 'Enter') this.handleCustomStatusSubmit();
              if (e.key === 'Escape') this.cancelCustomStatus();
            }}"
          />
          <div class="custom-status-actions">
            <button class="custom-status-btn secondary" @click="${this.cancelCustomStatus}">Cancel</button>
            <button class="custom-status-btn primary" @click="${this.handleCustomStatusSubmit}">Save</button>
          </div>
        </div>
      </div>
    `;
  }

  private renderQuickSettings() {
    if (!this.showQuickSettings) return '';

    return html`
      <div class="quick-settings-section" role="group" aria-label="Quick settings">
        <div class="quick-settings-label">Quick Settings</div>

        <div class="quick-setting-item">
          <div class="quick-setting-left">
            <svg class="quick-setting-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke-linecap="round" stroke-linejoin="round" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            <span class="quick-setting-label" id="qs-label-notifications">Notifications</span>
          </div>
          <div
            class="toggle-switch ${this.quickSettings.notifications ? 'on' : ''}"
            @click="${() => this.handleQuickSettingToggle('notifications')}"
            role="switch"
            tabindex="0"
            aria-checked="${this.quickSettings.notifications ? 'true' : 'false'}"
            aria-labelledby="qs-label-notifications"
            @keydown="${(e: KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.handleQuickSettingToggle('notifications');
              }
            }}"
          ></div>
        </div>

        <div class="quick-setting-item">
          <div class="quick-setting-left">
            <svg class="quick-setting-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            <span class="quick-setting-label" id="qs-label-darkmode">Dark Mode</span>
          </div>
          <div
            class="toggle-switch ${this.quickSettings.darkMode ? 'on' : ''}"
            @click="${() => this.handleQuickSettingToggle('darkMode')}"
            role="switch"
            tabindex="0"
            aria-checked="${this.quickSettings.darkMode ? 'true' : 'false'}"
            aria-labelledby="qs-label-darkmode"
            @keydown="${(e: KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.handleQuickSettingToggle('darkMode');
              }
            }}"
          ></div>
        </div>

        <div class="quick-setting-item">
          <div class="quick-setting-left">
            <svg class="quick-setting-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" stroke-linecap="round" stroke-linejoin="round" />
              <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            <span class="quick-setting-label" id="qs-label-sounds">Sounds</span>
          </div>
          <div
            class="toggle-switch ${this.quickSettings.sounds ? 'on' : ''}"
            @click="${() => this.handleQuickSettingToggle('sounds')}"
            role="switch"
            tabindex="0"
            aria-checked="${this.quickSettings.sounds ? 'true' : 'false'}"
            aria-labelledby="qs-label-sounds"
            @keydown="${(e: KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.handleQuickSettingToggle('sounds');
              }
            }}"
          ></div>
        </div>
      </div>
    `;
  }

  private renderCustomMenuItem(item: CustomMenuItem) {
    const classes = classMap({
      'menu-item': true,
      danger: item.danger || false,
    });

    const content = html`
      ${item.icon
        ? html`<span class="custom-icon" aria-hidden="true">${unsafeHTML(item.icon)}</span>`
        : html`<span class="menu-icon" aria-hidden="true"></span>`}
      ${item.label}
    `;

    if (item.href) {
      return html`
        ${item.dividerBefore ? html`<div class="divider" aria-hidden="true"></div>` : ''}
        <a href="${item.href}" class="${classes}" role="menuitem">${content}</a>
      `;
    }

    return html`
      ${item.dividerBefore ? html`<div class="divider" aria-hidden="true"></div>` : ''}
      <div
        class="${classes}"
        @click="${() => this.handleCustomItemClick(item)}"
        role="menuitem"
        tabindex="-1"
      >${content}</div>
    `;
  }

  private getCustomItemsByPosition(position: CustomMenuItem['position']) {
    return this.customMenuItems.filter((item) => item.position === position);
  }

  /** App-registered 'user-menu' region items, sorted by `order` asc (def 100). */
  private get sortedRegisteredMenuItems(): MenuItemSnapshot[] {
    return this.registeredMenuItems
      .slice()
      .sort((a, b) => (a.order ?? 100) - (b.order ?? 100));
  }

  private handleRegisteredItemClick(item: MenuItemSnapshot) {
    if (item.disabled) return;
    // Non-href items emit the shared menu-contribution select event.
    this.dispatchEvent(
      new CustomEvent('moya-menu-select', {
        bubbles: true,
        composed: true,
        detail: { itemId: item.id },
      })
    );
    this.isOpen = false;
  }

  private renderRegisteredMenuItem(item: MenuItemSnapshot) {
    const classes = classMap({
      'menu-item': true,
      disabled: item.disabled || false,
    });
    const showBadge = typeof item.badge === 'number' && item.badge > 0;
    const content = html`
      ${item.icon
        ? html`<span class="custom-icon" aria-hidden="true">${unsafeHTML(item.icon)}</span>`
        : html`<span class="menu-icon" aria-hidden="true"></span>`}
      <span class="menu-item-label">${item.label}</span>
      ${showBadge
        ? html`<span class="menu-item-badge" aria-label="${item.badge} new">${item.badge}</span>`
        : ''}
    `;

    if (item.href) {
      return html`
        <a
          href="${item.href}"
          class="${classes}"
          role="menuitem"
          tabindex="-1"
          aria-disabled="${item.disabled ? 'true' : 'false'}"
        >${content}</a>
      `;
    }

    return html`
      <div
        class="${classes}"
        @click="${() => this.handleRegisteredItemClick(item)}"
        role="menuitem"
        tabindex="-1"
        aria-disabled="${item.disabled ? 'true' : 'false'}"
        @keydown="${(e: KeyboardEvent) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            this.handleRegisteredItemClick(item);
          }
        }}"
      >${content}</div>
    `;
  }

  render() {
    const triggerClasses = classMap({
      trigger: true,
      open: this.isOpen,
      'avatar-only': this.avatarOnly,
    });

    const dropdownClasses = classMap({
      dropdown: true,
      open: this.isOpen,
    });

    const avatarClasses = classMap({
      avatar: true,
      gradient: true,
      [`size-${this.size}`]: true,
    });

    const initials = this.getInitials(this.user?.name, this.user?.email);
    const avatarColor = this.getAvatarColor(this.user?.name);

    const statusDotClasses = classMap({
      'status-dot': true,
      [this.currentStatus]: true,
      [`size-${this.size}`]: this.size !== 'md',
    });

    const userLabel = this.user?.name || this.user?.email || 'User';

    return html`
      <button
        class="${triggerClasses}"
        @click="${this.toggleDropdown}"
        @keydown="${this.handleTriggerKeydown}"
        aria-label="Open user menu for ${userLabel}"
        aria-expanded="${this.isOpen ? 'true' : 'false'}"
        aria-haspopup="menu"
        aria-controls="moya-user-menu-dropdown"
      >
        <div class="avatar-wrapper">
          <div class="${avatarClasses}" style="background: ${avatarColor}" aria-hidden="true">
            ${this.user?.avatarUrl
              ? html`<img src="${this.user.avatarUrl}" alt="" />`
              : initials}
          </div>
          ${this.showStatusSelector
            ? html`<span class="${statusDotClasses}" aria-hidden="true"></span>`
            : ''}
        </div>
        ${!this.avatarOnly
          ? html`
              <div class="user-info">
                <span class="user-name">${this.user?.name || this.user?.email || 'User'}</span>
                <span class="user-email">${this.user?.email || ''}</span>
              </div>
            `
          : ''}
        <svg class="chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <path d="M6 9l6 6 6-6" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>

      <div
        id="moya-user-menu-dropdown"
        class="${dropdownClasses}"
        role="menu"
        aria-label="User menu"
        @keydown="${this.handleMenuKeydown}"
      >
        <div class="profile-section">
          <div class="profile-avatar-wrapper">
            <div class="profile-avatar" style="background: ${avatarColor}" aria-hidden="true">
              ${this.user?.avatarUrl
                ? html`<img src="${this.user.avatarUrl}" alt="" style="width:100%;height:100%;border-radius:50%;object-fit:cover" />`
                : initials}
            </div>
            ${this.showStatusSelector
              ? html`<span class="profile-status-dot ${this.currentStatus}" aria-hidden="true"></span>`
              : ''}
          </div>
          <div class="profile-name">${this.user?.name || 'User'}</div>
          <div class="profile-email">${this.user?.email || ''}</div>
        </div>

        ${this.renderStatusSelector()}
        ${this.renderQuickSettings()}

        ${this.showOrganizations && this.user?.organizations && this.user.organizations.length > 1
          ? html`
              <div class="org-section" role="group" aria-label="Organizations">
                <div class="org-label">Organizations</div>
                ${this.user.organizations.map(
                  (org) => html`
                    <div
                      class="org-item ${org.id === this.user?.activeOrganizationId ? 'active' : ''}"
                      @click="${() => this.handleOrgSwitch(org)}"
                      role="menuitemradio"
                      tabindex="-1"
                      aria-checked="${org.id === this.user?.activeOrganizationId ? 'true' : 'false'}"
                      aria-label="Switch to ${org.name}"
                      @keydown="${(e: KeyboardEvent) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          this.handleOrgSwitch(org);
                        }
                      }}"
                    >
                      <div class="org-logo" aria-hidden="true">
                        ${org.logoUrl
                          ? html`<img src="${org.logoUrl}" alt="" style="width:100%;height:100%;border-radius:6px;object-fit:cover" />`
                          : org.name.charAt(0)}
                      </div>
                      <span class="org-name">${org.name}</span>
                      ${org.id === this.user?.activeOrganizationId ? html`<span class="org-check" aria-hidden="true">✓</span>` : ''}
                    </div>
                  `
                )}
              </div>
            `
          : ''}

        <div class="menu-section">
          <!-- Custom items at top -->
          ${this.getCustomItemsByPosition('top').map((item) => this.renderCustomMenuItem(item))}

          ${this.showProfile
            ? html`
                <a href="${this.profileUrl}" class="menu-item" role="menuitem">
                  <svg class="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" stroke-linecap="round" stroke-linejoin="round" />
                    <circle cx="12" cy="7" r="4" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                  Profile
                </a>
              `
            : ''}

          <!-- Custom items after profile -->
          ${this.getCustomItemsByPosition('after-profile').map((item) => this.renderCustomMenuItem(item))}

          ${this.showSettings
            ? html`
                <a href="${this.settingsUrl}" class="menu-item" role="menuitem">
                  <svg class="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                  </svg>
                  Settings
                </a>
              `
            : ''}

          ${this.showNotificationPrefs
            ? html`
                <a href="${this.notificationPrefsUrl}" class="menu-item" role="menuitem">
                  <svg class="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                  Notification Preferences
                </a>
              `
            : ''}

          ${this.showHelp
            ? html`
                <div class="divider" aria-hidden="true"></div>
                <a href="${this.helpUrl}" class="menu-item" role="menuitem">
                  <svg class="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" stroke-linecap="round" stroke-linejoin="round" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                  Help & Support
                </a>
              `
            : ''}

          <!-- App-registered 'user-menu' region items -->
          ${this.sortedRegisteredMenuItems.length > 0
            ? html`
                <div class="divider" aria-hidden="true"></div>
                ${this.sortedRegisteredMenuItems.map((item) => this.renderRegisteredMenuItem(item))}
              `
            : ''}

          <!-- Custom items before logout -->
          ${this.getCustomItemsByPosition('before-logout').map((item) => this.renderCustomMenuItem(item))}

          <div class="divider" aria-hidden="true"></div>

          <div
            class="menu-item danger"
            @click="${this.handleLogout}"
            role="menuitem"
            tabindex="-1"
            aria-label="Sign out"
            @keydown="${(e: KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.handleLogout();
              }
            }}"
          >
            <svg class="menu-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" stroke-linecap="round" stroke-linejoin="round" />
              <polyline points="16 17 21 12 16 7" stroke-linecap="round" stroke-linejoin="round" />
              <line x1="21" y1="12" x2="9" y2="12" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
            Sign out
          </div>

          <!-- Custom items at bottom -->
          ${this.getCustomItemsByPosition('bottom').map((item) => this.renderCustomMenuItem(item))}
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'moya-user-menu': MoyaUserMenu;
  }
}
